import { BehaviorSubject, Observable } from "rxjs";
import {
  CobrowseFilterLayoutType,
  ErrorCode,
  ICustomField,
  RegistrationTypeEnum,
  WidgetsEnum,
} from "../models";
import { HandledError } from "../models/HandledError";
import { ErrorHandler } from "./error.handler.service";
import { getAssetPath } from "@stencil/core";
import { ISchedule } from "@auvious/utils";
import ky from "ky";
import {
  IApplicationDTO,
  IApplicationConfig,
  PublicParam,
} from "../models/IApplication";
import { IConfigOptions as IIntegrationOptions } from "@auvious/integrations";
import { NotificationService } from "./notification.service";
import { ScheduleChannel, SchedulePicker } from "../models/schedule/ISchedule";
import { FilterType } from "@auvious/cobrowser";
import { Log } from "./log.service";

export interface IScheduleOptions {
  scheduleMode?: ScheduleChannel;
  schedulePicker?: SchedulePicker;
}

export interface IConfigOptions extends IIntegrationOptions, IScheduleOptions {
  /* handled by IntegrationOptions
   // genesys cloud
   pcEnvironment?: string;
   pcOrganizationId?: string;
   pcDeploymentId?: string;
   pcQueue?: string;
   pcCallbackQueueName?: string;

   // genesys premise
   dataUrl?: string;
   cometUrl?: string;
   cometChannel?: string;
   cometApiUrl?: string;

   // generic
   chatMode?:
     | "genesys-cloud"
     | "genesys-api"
     | "genesys-premise"
     | "genesys-web-messaging"
     | "talkdesk-digital-connect";
   kioskMode?: boolean;
   customerAvatarUrl?: string;
   customDomain?: string;
   applicationId?: string;
   auviousEnvironment?: string;
   mqttUrl?: string;

   // digital connect
   dcTouchpointId?: string;
  */

  applicationVerifySource?: boolean;

  auviousEventsEnvironment?: string;

  registrationType?: RegistrationTypeEnum;
  organization?: string;
  organizationId?: string;

  activeWidgets?: WidgetsEnum[];
  locale?: string;
  closeHidden?: boolean;
  popOutHidden?: boolean;
  incomingChatMessagesBlocked?: boolean;
  queueVideoUrl?: string;
  queueVideoDelaySeconds?: number;
  queueEmbeddableVideoCode?: string;
  customFields?: ICustomField[];
  launcherImageUrl?: string;
  customerDisplayName?: string;
  customerId?: string;
  customerMetadata?: { [key: string]: string };
  customerEmail?: string;
  customerSubject?: string;
  confirmCall?: boolean;
  greetingAction?: WidgetsEnum;
  greetingSoundOn?: boolean;
  notificationSoundUrl?: string;
  schedule?: ISchedule;
  captchaSiteKey?: string;
  scheduleIntervalMinutes?: number;
  scheduleOverlapCheck?: boolean;
  scheduleOverlapMaxConcurrent?: number;
  scheduleDisclaimerUrl?: string;
  appointmentNotificationHeaders?: { [key: string]: string };
  pcQueueId?: string;
  queueEstimateWaitTime?: boolean;
  queueMessagingEnabled?: boolean;
  videoMessagingEnabled?: boolean;
  chatAttachmentsEnabled?: boolean;
  chatNicknamesEnabled?: boolean;
  chatNoticesEnabled?: boolean;
  agentInfoEnabled?: boolean;
  cobrowseDisclaimerUrl?: string;
  cobrowseDisplayCapture?: boolean;
  endedNotificationEnabled?: boolean;
  ticketLength?: number;
  ticketGroupsEnabled?: boolean;
}

class AppConfigServiceCtrl {
  private _auviousUrl: string;

  private _options: IConfigOptions = {};

  private optionsReadySubject: BehaviorSubject<IConfigOptions>;

  private _cobrowseFilters: string[];
  private _allowedOrigins: string[];
  private _cobrowseFilterType: CobrowseFilterLayoutType;
  private _cobrowseFilterTypeMap: {
    [key: string]: FilterType;
  } = {
    blurBox: FilterType.DisplayRandomized,
    darkBox: FilterType.Display,
    hidden: FilterType.NoDisplay,
  };

  private _applicationConfig: IApplicationConfig;

  private _validateCustomFieldsFn: () => boolean;

  constructor() {
    // why behavior subject?
    this.optionsReadySubject = new BehaviorSubject(null);
  }

  public propsValid(): boolean {
    let error = false;

    const propError = (message, errorCode: ErrorCode) => {
      NotificationService.error(message, { ttl: -1, errorCode });
      error = true;
    };

    // props that are required for conversation specific widget types
    if (this.isConversationSupported) {
      if (this.isGenesys) {
        if (
          !this.pcEnvironment &&
          !this.pcWidgetScriptUrl &&
          !this.pcWidgetPluginsPath
        ) {
          propError(
            "pcEnvironment is missing",
            ErrorCode.INIT_MISSING_REQUIRED_PROPERTY
          );
        }
        if (this.queueEstimateWaitTime && !this.pcQueueId) {
          propError(
            "pc-queue-id is required for the queue-estimate-wait-time",
            ErrorCode.INIT_MISSING_REQUIRED_PROPERTY
          );
        }
      } else if (this.isTalkdesk) {
        if (!this.dcTouchpointId) {
          propError(
            "dcTouchpointId is missing",
            ErrorCode.INIT_MISSING_REQUIRED_PROPERTY
          );
        }
        if (
          this.registrationType === RegistrationTypeEnum.name &&
          (!this._options?.customerEmail || !this._options?.customerSubject)
        ) {
          propError(
            "Customer email and/or subject are missing",
            ErrorCode.INIT_MISSING_REQUIRED_PROPERTY
          );
        }
        if (this.registrationType === RegistrationTypeEnum.custom) {
          this._validateCustomFieldsFn = () => {
            let emailFound, subjectFound;
            this.customFields.forEach((field) => {
              emailFound = emailFound || field.name === "email";
              subjectFound = subjectFound || field.name === "subject";
            });
            if (!emailFound || !subjectFound) {
              propError(
                "Customer email and/or subject are missing",
                ErrorCode.INIT_MISSING_REQUIRED_PROPERTY
              );
              return false;
            }
            return true;
          };
        }
      } else if (this.isCXOneDFO) {
        if (!this.cxBrandId || !this.cxChannelId || !this.cxEnvironment) {
          propError(
            "nc-brand-id, nc-channel-id and nc-environment are required",
            ErrorCode.INIT_MISSING_REQUIRED_PROPERTY
          );
        }
      } else if (this.isVerintChat) {
        if (!this.vtServerUrl) {
          propError(
            "vt-server-url is required",
            ErrorCode.INIT_MISSING_REQUIRED_PROPERTY
          );
        }
      }

      if (
        ![
          RegistrationTypeEnum.custom,
          RegistrationTypeEnum.name,
          RegistrationTypeEnum.nameEmailSubject,
        ].includes(this.registrationType)
      ) {
        propError(
          "registrationType is not valid",
          ErrorCode.INIT_INVALID_REGISTRATION_TYPE
        );
      }

      // removed @1.7.15
      // if (!Config.genesysCloudEnvironments.includes(this.pcEnvironment)) {
      //   propError(
      //     'pcEnvironment is not a valid Genesys environment. Try "mypurecloud.de", "mypurecloud.ie" etc...',
      //     ErrorCode.INIT_INVALID_PC_ENVIRONMENT
      //   );
      // }

      // genesys cloud + API
      if (["genesys-api", "genesys-cloud"].includes(this.chatMode)) {
        if (!this.pcQueue) {
          propError(
            "pcQueue is missing",
            ErrorCode.INIT_MISSING_REQUIRED_PROPERTY
          );
        }
        if (!this.pcOrganizationId) {
          propError(
            "pcOrganizationId is missing",
            ErrorCode.INIT_MISSING_REQUIRED_PROPERTY
          );
        }
        if (!this.pcDeploymentId) {
          propError(
            "pcDeploymentId is missing",
            ErrorCode.INIT_MISSING_REQUIRED_PROPERTY
          );
        }
      }

      // genesys premise
      if (["genesys-premise"].includes(this.chatMode)) {
        if (!this.dataUrl) {
          if (!this.cometUrl) {
            propError(
              "one of data-url, comet-url must be defined",
              ErrorCode.INIT_MISSING_REQUIRED_PROPERTY
            );
          }
          if (!this.cometUrl) {
            propError(
              "if using 'comet', 'comet-url' must be defined",
              ErrorCode.INIT_MISSING_REQUIRED_PROPERTY
            );
          }
          if (!this.cometChannel) {
            propError(
              "if using 'comet', 'comet-channel' must be defined",
              ErrorCode.INIT_MISSING_REQUIRED_PROPERTY
            );
          }
        }
      }

      // v1.1
      if (
        ![
          "genesys-api",
          "genesys-cloud",
          "genesys-premise",
          "genesys-web-messaging",
          "talkdesk-digital-connect",
          "cxone-dfo-chat",
          "verint-chat",
        ].includes(this.chatMode)
      ) {
        propError(
          `invalid chatMode "${this.chatMode}"`,
          ErrorCode.INIT_MISSING_REQUIRED_PROPERTY
        );
      }
    }

    if (!this._options?.auviousEnvironment) {
      // has default so
      propError(
        "auviousEnvironment is missing",
        ErrorCode.INIT_MISSING_REQUIRED_PROPERTY
      );
    }

    if (
      !!this._options.greetingAction &&
      ![
        WidgetsEnum.audio,
        WidgetsEnum.video,
        WidgetsEnum.callback,
        WidgetsEnum.cobrowse,
        WidgetsEnum.chat,
        WidgetsEnum.screenshare,
      ].includes(this._options.greetingAction)
    ) {
      propError(
        "grettingAction is not valid",
        ErrorCode.INIT_INVALID_GREETING_ACTION
      );
    }

    if (this.isScheduleSupported) {
      if (!this.applicationId) {
        propError(
          "applicationId is missing",
          ErrorCode.INIT_MISSING_REQUIRED_PROPERTY
        );
      }
      if (!["standard", "timeslots"].includes(this.schedulePicker)) {
        propError(
          "schedulePicker is not valid",
          ErrorCode.INIT_MISSING_REQUIRED_PROPERTY
        );
      }
      switch (this.scheduleMode) {
        case "genesys-callback":
          if (!this._options?.pcCallbackQueueName) {
            propError(
              "pcCallbackQueue is required for callback",
              ErrorCode.INIT_MISSING_REQUIRED_PROPERTY
            );
          }
          break;
        case "talkdesk-appointment":
          if (!this.dcTouchpointId) {
            propError(
              "dcTouchpointId is required",
              ErrorCode.INIT_MISSING_REQUIRED_PROPERTY
            );
          }
          if (this.scheduleOverlapCheck && !this.organizationId) {
            propError(
              "organizationId is required",
              ErrorCode.INIT_MISSING_REQUIRED_PROPERTY
            );
          }
          break;
        case "genesys-appointment":
          if (!this.pcQueue && !this.pcCallbackQueueName) {
            propError(
              "pcCallbackQueue or pcQueue is required for an appointment",
              ErrorCode.INIT_MISSING_REQUIRED_PROPERTY
            );
          }
          if (!this.pcEnvironment) {
            propError(
              "pcEnvironment is required for an appointment",
              ErrorCode.INIT_MISSING_REQUIRED_PROPERTY
            );
          }
          if (!this.pcDeploymentId) {
            propError(
              "pcDeploymentId is required for an appointment",
              ErrorCode.INIT_MISSING_REQUIRED_PROPERTY
            );
          }
          if (!this.applicationId) {
            propError(
              "applicationId is required for an appointment",
              ErrorCode.INIT_MISSING_REQUIRED_PROPERTY
            );
          }
          break;
        default:
          propError(
            "scheduleMode is not valid",
            ErrorCode.INIT_MISSING_REQUIRED_PROPERTY
          );
      }
    }

    if (!!this.locale && this.locale.split("-").length < 2) {
      propError(
        `not a valid locale "${this.locale}". Ex. "el-GR"`,
        ErrorCode.INIT_MISSING_REQUIRED_PROPERTY
      );
    }

    return !error;
  }

  public optionsInstance(): IConfigOptions {
    return this._options;
  }

  public async setOptions(options: IConfigOptions) {
    this._options = options;
    if (options.applicationId) {
      try {
        await this.getApplicationConfig(options.applicationId);
      } catch (ex) {
        Log.error("Could not retrieve application config", ex);
      }
    }
    this.optionsReadySubject.next(this._options);
  }

  public get applicationConfig(): IApplicationConfig {
    return this._applicationConfig;
  }

  public updateOption(option: IConfigOptions) {
    this._options = { ...this._options, ...option };
    this.optionsReadySubject.next(this._options);
  }

  public get optionsReady(): Observable<IConfigOptions> {
    return this.optionsReadySubject.asObservable();
  }

  public setActiveWidgets(csv: string) {
    const activeWidgets: WidgetsEnum[] = [];
    if (!!csv) {
      const list = csv.split(",");
      list.forEach((i) => activeWidgets.push(WidgetsEnum[i]));
    }
    if (activeWidgets.length == 0) {
      // support version 1.0
      activeWidgets.push(WidgetsEnum.video);
    }
    this._options = { ...this._options, activeWidgets };
  }

  public get activeWidgets(): WidgetsEnum[] {
    return this._options.activeWidgets || [WidgetsEnum.video];
  }

  public get isConversationSupported(): boolean {
    return (
      this.activeWidgets.includes(WidgetsEnum.audio) ||
      this.activeWidgets.includes(WidgetsEnum.chat) ||
      this.activeWidgets.includes(WidgetsEnum.video)
    );
  }

  public get isGenesys(): boolean {
    return [
      "genesys-cloud",
      "genesys-api",
      "genesys-premise",
      "genesys-web-messaging",
    ].includes(this._options.chatMode);
  }

  public get isTalkdesk(): boolean {
    return this._options.chatMode === "talkdesk-digital-connect";
  }

  public get isCXOneDFO(): boolean {
    return this._options.chatMode === "cxone-dfo-chat";
  }

  public get isVerintChat(): boolean {
    return this._options.chatMode === "verint-chat";
  }

  public get isScheduleSupported(): boolean {
    return this.activeWidgets.includes(WidgetsEnum.callback);
  }

  public get customerAvatar() {
    return (
      this._options.customerAvatarUrl ||
      "https://genesys.auvious.com/assets/images/logo_app-icon.svg"
    );
  }

  public get customerDisplayName() {
    return this._options.customerDisplayName;
  }

  public get customerEmail() {
    return this._options?.customerEmail;
  }

  public get customerId() {
    return this._options?.customerId;
  }

  public get customerSubject() {
    return this._options?.customerSubject;
  }

  public get whitelistDomains() {
    return [
      "https://localhost:4200",
      "https://127.0.0.1:4200",
      this.auviousBaseUrl,
      this.customDomainURL,
    ];
  }
  public set iFrameOrigin(value: string) {
    if (!value) return;
    try {
      this._auviousUrl = new URL(value).origin;
    } catch (ex) {
      ErrorHandler.log(
        new HandledError(ex, ErrorCode.CONVERSATION_URL_PARSE_FAILED)
      );
    }
  }
  public get iFrameOrigin() {
    return this._auviousUrl;
  }

  public get applicationId() {
    return this._options?.applicationId;
  }
  public get applicationVerifySource() {
    return this._options?.applicationVerifySource;
  }
  public get cxBrandId(): number {
    return this._options?.cxBrandId;
  }
  public get cxChannelId(): string {
    return this._options?.cxChannelId;
  }
  public get cxEnvironment(): IConfigOptions["cxEnvironment"] {
    return this._options?.cxEnvironment;
  }
  public get vtServerUrl(): string {
    return this._options?.vtServerUrl;
  }
  public get vtTenantId(): string {
    return this._options?.vtTenantId;
  }
  public get cxAuthorizationCode(): string {
    return this._options?.cxAuthorizationCode;
  }
  public get cxCustomEnvironment(): IConfigOptions["cxCustomEnvironment"] {
    return this._options?.cxCustomEnvironment;
  }
  public get pcQueue() {
    return this._options?.pcQueue;
  }
  public get pcQueueId() {
    return this._options?.pcQueueId;
  }
  public get pcCallbackQueueName() {
    return this._options?.pcCallbackQueueName;
  }
  public get pcEnvironment() {
    return this._options?.pcEnvironment;
  }
  public get pcWidgetScriptUrl() {
    return this._options?.pcWidgetScriptUrl;
  }
  public get pcWidgetPluginsPath() {
    return this._options?.pcWidgetPluginsPath;
  }
  public get pcOrganizationId() {
    return this._options?.pcOrganizationId;
  }
  public get organizationId() {
    return this._options.organizationId;
  }
  public get pcDeploymentId() {
    return this._options?.pcDeploymentId;
  }
  public get auviousBaseUrl() {
    return this._options && "https://" + this._options.auviousEnvironment;
  }
  public get auviousWebSocketUrl() {
    return [
      "c249c541-d492-4953-8958-9c0d37933662", // BI guatemala (they have events.auvious and dont want to change it)
      "6e6d5224-1909-48fa-a982-66cef3fa4c08", // auvious (.de) organisation for testing
    ].includes(this._options?.pcOrganizationId)
      ? `events.${this._options.auviousEnvironment}`
      : this._options.auviousEventsEnvironment ||
          this._options.auviousEnvironment;
  }
  public get chatMode() {
    return this._options?.chatMode;
  }
  public get country() {
    return this._options?.locale ? this._options.locale.split("-")[1] : "US";
  }
  public get language() {
    return this._options?.locale ? this._options.locale.split("-")[0] : "en";
  }
  public get kioskMode() {
    return this._options?.kioskMode;
  }
  public get locale() {
    return this._options?.locale ?? `${this.language}-${this.country}`;
  }
  public get organization() {
    return this._options?.organization;
  }
  public get keepConversationAliveInterval() {
    return 1000 * 60 * 5; /* every 5 minutes */
  }

  public get canPopOut() {
    return (
      !this._options?.popOutHidden &&
      this._options?.chatMode === "genesys-cloud" &&
      !this.kioskMode
    );
  }
  public get dataUrl() {
    return this._options?.dataUrl;
  }
  public get cometUrl() {
    return this._options?.cometUrl;
  }
  public get cometChannel() {
    return this._options?.cometChannel;
  }
  public get cometApiUrl() {
    return this._options?.cometApiUrl;
  }
  public set cobrowseFilters(values: string[]) {
    this._cobrowseFilters = values;
  }
  public get cobrowseFilters(): string[] {
    return this._cobrowseFilters || [];
  }
  public get allowedOrigins(): string[] {
    return this._allowedOrigins || [];
  }
  public set allowedOrigins(origins: string[]) {
    this._allowedOrigins = origins;
  }

  public set cobrowseFilterLayoutType(type: CobrowseFilterLayoutType) {
    this._cobrowseFilterType = type;
  }
  public get cobrowseFilterType(): FilterType {
    return (
      this._cobrowseFilterTypeMap[this._cobrowseFilterType] ||
      FilterType.Display
    );
  }
  public get closeHidden() {
    return this._options?.closeHidden;
  }
  public get incomingChatMessagesBlocked() {
    return this._options?.incomingChatMessagesBlocked;
  }
  public get queueVideoUrl() {
    return this._options?.queueVideoUrl;
  }
  public get queueVideoDelaySeconds() {
    return this._options?.queueVideoDelaySeconds;
  }
  public get queueEmbeddableVideoCode() {
    return this._options?.queueEmbeddableVideoCode;
  }
  public get queueEstimateWaitTime() {
    return this._options.queueEstimateWaitTime;
  }
  public get customFields() {
    return this._options?.customFields || [];
  }
  public get customerMetadata() {
    return this._options?.customerMetadata || {};
  }
  public get scheduleOverlapCheck(): boolean {
    return !!this._options?.scheduleOverlapCheck;
  }
  public get scheduleOverlapMaxConcurrent(): number {
    return this._options?.scheduleOverlapMaxConcurrent || 1;
  }
  public get launcherImageUrl() {
    return this._options?.launcherImageUrl;
  }

  public get genesysCloudEnvironments() {
    return [
      "mypurecloud.de",
      "mypurecloud.ie",
      "euw2.pure.cloud",
      "mypurecloud.jp",
      "mypurecloud.com.au",
      "apne2.pure.cloud",
      "usw2.pure.cloud",
      "mypurecloud.com",
      "cac1.pure.cloud",
    ];
  }

  public get schedule(): ISchedule {
    return (
      (!!this._options?.schedule &&
        JSON.parse(JSON.stringify(this._options.schedule))) || {
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        schedule: {},
      }
    );
  }

  public get scheduleIntervalMinutes(): number {
    return this._options.scheduleIntervalMinutes;
  }

  public get hasSchedule() {
    return (
      this.schedule?.schedule?.open?.length > 0 ||
      this.schedule?.schedule?.closed?.length > 0
    );
  }

  public getGreetingSoundOn() {
    return this._options?.greetingSoundOn ?? true;
  }

  public getNotificationSoundUrl() {
    return (
      this._options?.notificationSoundUrl ||
      getAssetPath(`auvious-assets/notification.mp3`)
    );
  }

  public get customDomainURL() {
    return this._applicationConfig?.publicParameters?.customDomainURL;
  }

  public get customDomainHost() {
    return this.customDomainURL?.split("//")?.[1] || this.customDomainURL;
  }

  public get captchaSiteKey() {
    return this._options?.captchaSiteKey;
  }

  public get schedulePicker() {
    return this._options?.schedulePicker;
  }

  public get scheduleMode() {
    return this._options?.scheduleMode;
  }

  public get scheduleDisclaimerURL() {
    return this._options?.scheduleDisclaimerUrl;
  }

  public get cobrowseDisclaimerURL() {
    return this._options?.cobrowseDisclaimerUrl;
  }

  public get cobrowseDisplayCaptureEnabled() {
    return this._options?.cobrowseDisplayCapture;
  }

  public get dcTouchpointId() {
    return this._options?.dcTouchpointId;
  }

  public get registrationType() {
    return this._options?.registrationType;
  }

  public validateCustomFieldsFn(): boolean {
    if (this._validateCustomFieldsFn) {
      const valid = this._validateCustomFieldsFn();
      this._validateCustomFieldsFn = undefined;
      return valid;
    }
    return true;
  }

  public get supportsCallReconnectOnRefresh(): boolean {
    return ["genesys-cloud", "genesys-premise"].includes(
      this._options?.chatMode
    );
  }

  public get pcAutoStartConversationEnabled() {
    return (
      this._options.pcGenesysWebMessagingConfiguration?.messenger?.apps
        ?.conversations?.autoStart?.enabled ?? false
    );
  }

  public get webMessagingAttachmentsEnabled() {
    return (
      // @ts-expect-error
      this._options.pcGenesysWebMessagingConfiguration?.messenger?.fileUpload?.enableAttachments ?? true
    );
  }

  public get webMessagingAttachmentFileTypes(): string {
    return this._options.pcGenesysWebMessagingConfiguration?.messenger?.fileUpload?.modes?.[0]?.fileTypes?.join(
      ","
    );
  }

  public get webMessagingAttachmentFileSize(): number {
    return (
      this._options.pcGenesysWebMessagingConfiguration?.messenger?.fileUpload
        ?.modes?.[0]?.maxFileSizeKB * 1024
    );
  }

  public get webMessagingAttachmentFileSizeUnit(): string {
    return (
      this._options.pcGenesysWebMessagingConfiguration?.messenger?.fileUpload
        ?.modes?.[0]?.maxFileSizeKB /
        1024 +
      "MB"
    );
  }

  public get queueMessagingEnabled(): boolean {
    return this._options.queueMessagingEnabled;
  }

  public get videoMessagingEnabled(): boolean {
    return this._options.videoMessagingEnabled;
  }

  public get chatAttachmentsEnabled(): boolean {
    return this._options.chatAttachmentsEnabled;
  }

  public get chatNicknamesEnabled(): boolean {
    return this._options.chatNicknamesEnabled;
  }

  public get chatNoticesEnabled(): boolean {
    return this._options.chatNoticesEnabled;
  }

  public get agentInfoEnabled(): boolean {
    return this._options.agentInfoEnabled;
  }

  public get endedNotificationEnabled(): boolean {
    return this._options.endedNotificationEnabled;
  }

  public get appointmentNotificationHeaders() {
    return this._options.appointmentNotificationHeaders;
  }

  public get ticketLength() {
    return 6; // this._options.ticketLength ?? 6; // not used for now. 9 chars + 2 groups is too much
  }

  public get ticketGroupsEnabled() {
    return this._options.ticketGroupsEnabled ?? true;
  }

  private async getApplicationConfig(applicationId: string): Promise<void> {
    try {
      const res: IApplicationDTO = await ky
        .get(`${this.auviousBaseUrl}/security/applications/${applicationId}`)
        .json();
      this._applicationConfig = res.config;
      this._options = {
        ...this._options,
        customDomain:
          this._applicationConfig?.publicParameters?.[
            PublicParam.CUSTOM_DOMAIN_URL
          ],
        pcGenesysWebMessagingConfiguration:
          this._applicationConfig?.publicParameters?.[
            PublicParam.GENESYS_WEB_MESSAGING_CONFIG
          ],
        ticketGroupsEnabled:
          this._applicationConfig?.publicParameters?.[
            PublicParam.TICKET_GROUPS_ENABLED
          ],
        ticketLength:
          this._applicationConfig?.publicParameters?.[
            PublicParam.TICKET_LENGTH
          ],
      };
    } catch (ex) {
      throw ex;
    }
  }
}

export const AppConfigService = new AppConfigServiceCtrl();
